import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentLayout } from 'components/Layouts'
import { useFetchMonthlyPremiums } from 'queries/premiums'

import classes from './PremiumsMonthly.module.scss'
import { ToggleCollapsibleCards } from '../../components/monthlyPremiums/ToggleCollapsibleCards/ToggleCollapsibleCards'
import CurrentMonthlyPremiumCard from '../../components/monthlyPremiums/CurrentMonthlyPremiumCard/CurrentMonthlyPremiumCard'
import Skeleton from 'react-loading-skeleton'
import { Alert } from '@cmsgov/design-system'
import MonthlyPremiumCard from 'containers/Premiums/components/monthlyPremiums/MonthlyPremiumCard/MonthlyPremiumCard'
import { BackToPremiumsBreadcrumb } from 'containers/Premiums/components/BackToPremiumsBreadcrumb'
import { BackToPremiumsLink } from 'containers/Premiums/components/BackToPremiums'
import { useEvent } from 'hooks/useEvent'
import { Component } from '@cms-max/medicare-api-sdk'

export const PremiumsMonthly: FC = () => {
  const { t } = useTranslation('premiumsMonthly')

  const { data, isError, isLoading, isSuccess } = useFetchMonthlyPremiums()

  const [openItems, setOpenItems] = useState<string[]>([])

  const history = data ?? []
  const current = useMemo(() => {
    return data?.find(item => item.isCurrent)
  }, [data])

  const { fireEvent } = useEvent('premiumMonthly')
  const handleOpenAll = () => {
    fireEvent('premiums_monthlypremiums_openalldetails_click')
    const items = data?.map(item => `${item.date.year}-${item.date.month}`)
    setOpenItems(items || [])
  }
  const handleCloseAll = () => {
    fireEvent('premiums_monthlypremiums_closealldetails_click')
    setOpenItems([])
  }
  /**
   * @param id
   * @param premiumDate Used for event tracking
   * @returns void
   */
  const handleToggleOne = (id: string, premiumDate: string) => () => {
    if (openItems.includes(id)) {
      fireEvent('premiums_monthlypremiums_opendetails_accordionclose', { date: premiumDate })
      setOpenItems((prev) => prev.filter(item => item !== id))
    } else {
      fireEvent('premiums_monthlypremiums_opendetails_accordionopen', { date: premiumDate })
      setOpenItems((prev) => [...prev, id])
    }
  }

  return (
    <ContentLayout
      className="ds-u-padding-bottom--7"
      heading={t('heading')}
      width="wide"
      withPrintButton
      printComponent={Component.Premiums}
      nav={<BackToPremiumsBreadcrumb />}
    >
      <div className='ds-l-container'>
        {isError && (
          <Alert variation='error' heading={t('errorAlert.heading')}>{t('errorAlert.content')}</Alert>
        )}

        {isSuccess && history.length === 0 && (
          <>
            <Alert
              heading={t('noRecordsAlert.heading')}
              className='ds-u-measure--wide ds-u-margin-bottom--3'>
                {t('noRecordsAlert.content')}
            </Alert>
            <BackToPremiumsLink />
          </>
        )}

        {!isError && (
          <div className={classes.layout}>
            <div className={classes.controls}>
              {isSuccess && history.length > 0 && <ToggleCollapsibleCards onClose={handleCloseAll} onOpen={handleOpenAll} />}
              {isLoading && (
                <Skeleton height={23} width={140} count={2} inline={true} containerClassName='ds-u-display--flex ds-u-justify-content--end my-u-gap--12' />
              )}
            </div>

            <div className={classes.current}>
              {current && <CurrentMonthlyPremiumCard monthlyPremium={current} />}
              {isLoading && <Skeleton height={542} />}
            </div>

            <div className={classes.history}>
              {isLoading && <Skeleton height={95} count={5} className='ds-u-margin-bottom--2' />}

              {!isLoading && history.length > 0 && history.map(item => {
                const formattedDate = `${item.date.year}-${item.date.month}`
                return <MonthlyPremiumCard
                  monthlyPremium={item}
                  key={formattedDate}
                  open={openItems.includes(formattedDate)}
                  onChange={handleToggleOne(formattedDate, formattedDate)}
                  className='ds-u-margin-bottom--2'
                />
              })}
            </div>
          </div>
        )}
      </div>
    </ContentLayout>
  )
}
